import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Spinner, Button, Row, Col } from 'react-bootstrap';
import { getClassById } from '../../api/classes';
import { SEO } from '../../components/SEO';
import DummyAvatar from '../../assets/images/avatar.png';
import './ClassDetail.scss';

// ── Fallback image ──
const CLASS_FALLBACK = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=400&fit=crop';

export const ClassDetailGuest = () => {
  const location = useLocation();
  const id = location.state?.id;
  const navigate = useNavigate();

  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    fetchClassDetails();
  }, [id]);

  const fetchClassDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getClassById(id);

      if (res && (res.data || res.class)) {
        const classObj = res.data?.class || res.data || res.class;
        setClassData(classObj);
      } else {
        setError('Class not found');
      }
    } catch (err) {
      console.warn('API fetch failed:', err);
      setError('Failed to load class details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const text = `Check out ${classData?.title || 'this class'} on Yoospeak!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: classData?.title || 'Class', text, url: shareUrl });
      } catch (err) {
        console.log('Share failed:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  if (error || (!loading && !classData)) {
    return (
      <Container className="py-5 text-center">
        <div style={{ padding: '60px 20px' }}>
          <i className="bi bi-exclamation-circle d-block mb-3" style={{ fontSize: '3rem', color: '#6B7280' }}></i>
          <h5 className="fw-bold mb-2">{error || 'Class not found'}</h5>
          <Button variant="primary" className="rounded-pill px-4 py-2 mt-2" onClick={() => navigate('/classes')} style={{ backgroundColor: '#6C3BFF', border: 'none' }}>
            Back to Classes
          </Button>
        </div>
      </Container>
    );
  }

  const instructor = classData?.instructor || classData?.teacher;
  const teacherName = (instructor && typeof instructor === 'object' && instructor.full_name)
    ? instructor.full_name
    : 'Yoospeak Instructor';
  const teacherAvatar = (instructor && typeof instructor === 'object' && instructor.profile_picture)
    ? instructor.profile_picture
    : DummyAvatar;

  const isObjectId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val);
  const languageName = (classData?.language && typeof classData.language === 'object')
    ? classData.language.name
    : (classData?.language && !isObjectId(classData.language) ? classData.language : 'English');

  const enrolledCount = classData?.students?.length || 0;
  const description = classData?.description || '';
  const shortDesc = description.length > 220 ? description.slice(0, 220) + '...' : description;

  const localStart = classData?.start_date ? new Date(classData.start_date) : null;
  const localEnd = classData?.end_date ? new Date(classData.end_date) : null;

  const startDateStr = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleDateString('en-GB')
    : 'TBD';

  const startTimeStr = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : (classData?.start_time || 'TBD');

  const endTimeStr = localEnd && !isNaN(localEnd.getTime())
    ? localEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : (classData?.end_time || 'TBD');

  return (
    <div className="community-detail-page class-detail-page py-4 position-relative">
      <SEO title={classData?.title || 'Class'} description={classData?.description || 'Class Detail'} image={classData?.cover_image || classData?.image} />

      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center" style={{
          zIndex: 9999,
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'all 0.3s ease-in-out'
        }}>
          <div className="p-4 rounded-4 shadow bg-white bg-opacity-75 text-center d-flex flex-column align-items-center" style={{ border: '1px solid rgba(255, 255, 255, 0.5)' }}>
            <Spinner animation="border" style={{ color: '#6C3BFF', width: '3rem', height: '3rem' }} />
            <span className="mt-3 fw-bold text-navy" style={{ letterSpacing: '0.5px' }}>Loading class...</span>
          </div>
        </div>
      )}

      {classData && (
        <Container fluid="xl">
          {/* Top Header Banner Box */}
          <section className="profile-banner-section bg-white border rounded-4 overflow-hidden mb-4">
            <div className="cd-banner">
              <img
                src={classData.cover_image || classData.image || CLASS_FALLBACK}
                alt={classData.title}
                className="cd-banner__img"
                onError={(e) => { e.target.src = CLASS_FALLBACK; }}
              />
              <div className="cd-banner__actions">
                <button className="cd-btn-share" onClick={handleShare}>
                  <i className="bi bi-share"></i> Share
                </button>
              </div>
            </div>

            <div className="cd-info-card bg-white p-4">
              <div className="cd-info-top">
                <div className="cd-logo-wrap">
                  <img
                    src={teacherAvatar}
                    alt={teacherName}
                    className="cd-logo border border-3 border-white rounded-circle object-fit-cover shadow"
                    onError={(e) => { e.target.src = DummyAvatar; }}
                  />
                </div>

                <div className="cd-info-body">
                  <div className="cd-name text-dark mb-1">{classData.title}</div>
                  <p className="cd-subtitle d-flex align-items-center gap-1">
                    Instructor: {teacherName}
                    {instructor?.is_blue_verified && <i className="bi bi-patch-check-fill text-primary" style={{ fontSize: '0.85rem' }} title="Verified Teacher" />}
                  </p>
                  <div className="cd-tags">
                    <span className="cd-tag cd-tag--lang">
                      <i className="bi bi-translate"></i> {languageName}
                    </span>
                    <span className="cd-tag cd-tag--students">
                      <i className="bi bi-people-fill"></i> {enrolledCount} / {classData.max_students || 1} Seats Enrolled
                    </span>
                    <span className="cd-tag cd-tag--cat">
                      <i className="bi bi-cash"></i> {classData.cost > 0 ? `$${classData.cost} Class` : 'Free Class'}
                    </span>
                  </div>
                </div>

                <div className="cd-info-actions">
                  <Button className="cd-btn-joined border-0 px-4 py-2" onClick={handleLoginRedirect} style={{ backgroundColor: '#6C3BFF', fontWeight: '600' }}>
                    Login to Join
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <Row className="g-4">
            <Col lg={8}>
              <div className="cd-about bg-white border rounded-4 p-4 shadow-sm mb-4">
                <h5 className="fw-bold mb-3 text-navy d-flex align-items-center border-bottom pb-3">
                  <i className="bi bi-info-circle me-2 text-primary fs-4"></i>
                  About Class
                </h5>
                <div className="desc-box bg-light rounded-4 p-4 mb-4">
                  <h6 className="fw-bold text-secondary mb-3 text-uppercase" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>Description</h6>
                  <div className="text-dark fs-6" style={{ lineHeight: '1.7' }}>
                    {descExpanded ? description : shortDesc}
                  </div>
                  {description.length > 220 && (
                    <button
                      onClick={() => setDescExpanded(!descExpanded)}
                      className="btn btn-link text-primary p-0 mt-2 fw-semibold text-decoration-none"
                    >
                      {descExpanded ? 'Show Less' : 'Read More...'}
                    </button>
                  )}
                </div>

                {classData.keypoints && classData.keypoints.length > 0 && (
                  <div className="topics-box bg-light rounded-4 p-4 mb-4">
                    <h6 className="fw-bold text-secondary mb-3 text-uppercase" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>Key Learnings</h6>
                    <ul className="list-unstyled d-flex flex-column gap-3 mb-0">
                      {classData.keypoints.map((point, index) => (
                        <li key={index} className="d-flex align-items-start gap-2 fs-6 text-gray-700">
                          <i className="bi bi-check-circle-fill text-primary mt-1"></i>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="text-center p-4 bg-light rounded-4 border">
                <h5 className="fw-bold text-dark mb-3">Want to access class materials?</h5>
                <p className="text-secondary mb-4">Log in to join the live session, view recordings, attendance, and documents!</p>
                <Button variant="primary" className="rounded-pill px-5 py-2 shadow-sm" onClick={handleLoginRedirect} style={{ backgroundColor: '#6C3BFF', border: 'none' }}>
                  Login / Sign Up
                </Button>
              </div>
            </Col>

            <Col lg={4}>
              <div className="cd-panel mb-4 bg-white border rounded-4 p-4 shadow-sm">
                <h5 className="fw-bold mb-3 border-bottom pb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-calendar-event text-primary"></i> Class Schedule
                </h5>
                <div className="d-flex flex-column gap-3 fs-6 mt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-gray-500">Start Date:</span>
                    <strong className="text-gray-900">{startDateStr}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-gray-500">Start Time:</span>
                    <strong className="text-gray-900">{startTimeStr}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-gray-500">End Time:</span>
                    <strong className="text-gray-900">{endTimeStr}</strong>
                  </div>
                </div>
              </div>

              <div className="cd-panel mb-4 bg-white border rounded-4 p-4 shadow-sm">
                <h5 className="fw-bold mb-3 border-bottom pb-3">Class Sizing</h5>
                <div className="d-flex flex-column gap-3 fs-6 mt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-gray-500">Class Size:</span>
                    <strong className="text-gray-900">{classData.max_students || 1}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-gray-500">Students Enrolled:</span>
                    <strong className="text-gray-900">{enrolledCount}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-gray-500">Seats Left:</span>
                    <strong className="text-primary">{Math.max(0, (classData.max_students || 1) - enrolledCount)}</strong>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
};
